// src/components/realtime/AuthenticatedSocketLifecycle.jsx
//
// AuthenticatedSocketLifecycle
// ============================
//
// PURPOSE
// -------
// Keep the Socket.IO singleton aligned with the authenticated internal shell.
//
// This component is intentionally small and conservative. It does NOT poll
// business data, it does NOT call /my-page, and it does NOT run a permanent
// liveness loop.
//
// It has only two responsibilities:
// 1) Request the socket connection while an authenticated user is inside the
//    internal application shell.
// 2) React to a backend runtime fingerprint change observed by apiClient through
//    an already-existing REST response.
//
// WHY THIS EXISTS
// ---------------
// Manual navigation away from Dashboard and back was observed to "wake up" the
// socket after a backend restart. That route switch re-enters the internal
// frontend lifecycle and causes the socket handshake to be refreshed.
//
// This component implements the same architectural idea without a hidden route
// change and without UI flicker: when apiClient detects that the backend process
// runtime changed, the socket client performs one clean reconnect with the
// current tab-local session/device/browser identity.
//
// IMPORTANT SAFETY RULES
// ----------------------
// - No REST polling.
// - No /my-page fetches.
// - No permanent whoami/liveness loop.
// - No logout decisions.
// - No session discovery or cross-tab recovery.
// - AuthContext remains the owner of authentication state.

import React, { useContext, useEffect } from "react";

import AuthContext from "../../context/AuthContext";
import { BACKEND_RUNTIME_CHANGED_EVENT } from "../../utils/apiClient";
import socketClient from "../../utils/socketClient";

const DEV = process.env.NODE_ENV !== "production";

function dlog(message, payload) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.log(message, payload || "");
}

function hasAuthenticatedSocketContext({ authChecked, authState, tabSessionId }) {
  return Boolean(
    authChecked &&
      authState?.isAuthenticated &&
      authState?.user?.id != null &&
      tabSessionId
  );
}

const AuthenticatedSocketLifecycle = () => {
  const { authState, authChecked, tabSessionId } = useContext(AuthContext);

  const shouldMaintainSocket = hasAuthenticatedSocketContext({
    authChecked,
    authState,
    tabSessionId,
  });

  const userId = authState?.user?.id || null;

  useEffect(() => {
    if (!shouldMaintainSocket) {
      return undefined;
    }

    dlog("[AuthenticatedSocketLifecycle] internal socket lifecycle active", {
      userId,
      hasTabSessionId: Boolean(tabSessionId),
    });

    /**
     * Normal internal-shell socket activation.
     *
     * Dashboard also calls socketClient.connect() today. Keeping this call here
     * is intentional and idempotent: it gives non-Dashboard internal routes the
     * same ability to maintain the realtime singleton, which is exactly what a
     * manual route switch appeared to fix during testing.
     */
    socketClient.connect();

    /**
     * Backend runtime change recovery.
     *
     * apiClient emits this event only when a normal REST response shows that
     * this tab is now talking to a different backend process runtime. We then
     * ask socketClient to rebuild its transport connection.  We do not refresh
     * business data here; Dashboard remains the owner of /my-page.
     */
    const handleBackendRuntimeChanged = (event) => {
      const detail = event?.detail || {};

      dlog("[AuthenticatedSocketLifecycle] backend runtime changed -> socket reconnect", {
        runtimeId: detail?.runtimeId || null,
        previousRuntimeId: detail?.previousRuntimeId || null,
        userId,
      });

      socketClient.handleBackendRuntimeChanged?.(detail);
    };

    window.addEventListener(
      BACKEND_RUNTIME_CHANGED_EVENT,
      handleBackendRuntimeChanged
    );

    return () => {
      window.removeEventListener(
        BACKEND_RUNTIME_CHANGED_EVENT,
        handleBackendRuntimeChanged
      );
    };
  }, [shouldMaintainSocket, userId, tabSessionId]);

  return null;
};

export default AuthenticatedSocketLifecycle;
